import type { ChatbotViewModel } from './chatbot.viewmodel';
import type { ChatMessageContent } from './chatbot.types';
import type { FlowContext, FlowState, FlowStepDefinition, FlowStepRef } from './chatbot-flow.types';

/**
 * Generic flow execution engine for the chatbot.
 *
 * Drives a `ChatbotViewModel` through a list of `FlowStepDefinition`s:
 * messages, action buttons, input prompts, and async handlers.
 *
 * The host app supplies:
 * - steps via `setSteps()`
 * - optional callbacks via `onTriggerCompleted` and `onContextUpdated`
 */
export class ChatbotFlowEngine {
  private chatbot: ChatbotViewModel;
  private flowSteps: FlowStepDefinition[] = [];
  private flowState: FlowState = ChatbotFlowEngine.createInitialState();
  private sequenceCounter = 0;

  /** Called when a trigger-based step is completed (e.g. modal confirms data). */
  onTriggerCompleted?: (triggerId: string, data?: FlowContext) => void;

  /** Called whenever the flow context is updated (async step result, user input, etc.). */
  onContextUpdated?: (context: FlowContext) => void;

  /** Called when the user submits free-text input during an 'input' step (before advancing). */
  onInputReceived?: (text: string, step: FlowStepDefinition) => void;

  constructor(chatbot: ChatbotViewModel) {
    this.chatbot = chatbot;
    this.chatbot.setOnUserMessage((text) => this.handleUserInput(text));
    this.chatbot.setOnMultipleSelectConfirm((messageId, optionIds) =>
      this.handleMultipleSelectConfirm(messageId, optionIds)
    );
  }

  // ── Static helpers ──────────────────────────────────────────────────────

  static readonly initialState: FlowState = {
    currentStepIndex: 0,
    currentStep: null,
    completedSteps: [],
    context: {},
    isWaitingForTrigger: null,
    isWaitingForInput: false,
  };

  /** Return a fresh copy of the initial flow state (safe to mutate). */
  static createInitialState(): FlowState {
    return {
      ...ChatbotFlowEngine.initialState,
      completedSteps: [],
      context: {},
    };
  }

  // ── Sequence management ────────────────────────────────────────────────

  /** Assign sequence numbers to steps that don't have one yet. */
  private assignSequences(steps: FlowStepDefinition[]) {
    for (const step of steps) {
      if (step.sequence == null) {
        step.sequence = this.sequenceCounter++;
      }
    }
  }

  /** Build a lightweight FlowStepRef from a full step definition. */
  private static toRef(step: FlowStepDefinition): FlowStepRef {
    const ref: FlowStepRef = { id: step.id, sequence: step.sequence ?? 0 };
    if (step.meta) ref.meta = step.meta;
    return ref;
  }

  // ── Step management ────────────────────────────────────────────────────

  /** Replace the entire flow definition. */
  setSteps(steps: FlowStepDefinition[]) {
    this.flowSteps = [...steps];
    this.assignSequences(this.flowSteps);
  }

  /** Get the current flow steps (e.g. for serialization). */
  getSteps(): FlowStepDefinition[] {
    return this.flowSteps;
  }

  /** Get the step at a given index. */
  getStepAt(index: number): FlowStepDefinition | undefined {
    return this.flowSteps[index];
  }

  /** Insert new steps after a given step (matched by sequence). */
  insertStepsAfter(afterSequence: number, newSteps: FlowStepDefinition[]) {
    const index = this.flowSteps.findIndex((step) => step.sequence === afterSequence);
    if (index === -1) return;
    this.assignSequences(newSteps);
    this.flowSteps.splice(index + 1, 0, ...newSteps);
  }

  /** Insert new steps immediately after the current step (for dynamic branching). */
  insertStepsAfterCurrent(newSteps: FlowStepDefinition[]) {
    const currentIndex = this.flowState.currentStepIndex;
    this.assignSequences(newSteps);
    this.flowSteps.splice(currentIndex + 1, 0, ...newSteps);
  }

  /** Remove all steps after the current step (prevents dangling steps when branching). */
  clearRemainingSteps() {
    this.flowSteps.splice(this.flowState.currentStepIndex + 1);
  }

  /** Remove a completed step by its base ID (e.g. to reopen a finished flow). */
  removeCompletedStep(stepId: string) {
    const index = this.flowState.completedSteps.findIndex((s) => s.id === stepId);
    if (index !== -1) {
      this.flowState.completedSteps.splice(index, 1);
    }
  }

  // ── Undo ───────────────────────────────────────────────────────────────

  /**
   * Revert to the previous step (undo last answer).
   * Only valid when the previous step has `revertable: true`.
   */
  revertToPreviousStep(): boolean {
    const prevIndex = this.flowState.currentStepIndex - 1;
    if (prevIndex < 0) return false;

    const prevStep = this.flowSteps[prevIndex];
    if (!prevStep?.revertable || !prevStep.waitForTrigger) return false;

    // 1. Remove last completed step
    this.flowState.completedSteps.pop();

    // 2. Move index back and restore waiting state
    this.flowState.currentStepIndex = prevIndex;
    this.flowState.currentStep = ChatbotFlowEngine.toRef(prevStep);
    this.flowState.isWaitingForTrigger = prevStep.waitForTrigger;
    this.flowState.isWaitingForInput = false;

    // 3. Animate out the current question + user echo, then re-enable previous actions
    this.chatbot.revertMessages(2);

    // 4. Notify state changed (triggers persist)
    this.notifyStateChanged();

    return true;
  }

  // ── State access ───────────────────────────────────────────────────────

  /** Get the current flow state. */
  getState(): FlowState {
    return this.flowState;
  }

  /** Get the accumulated flow context. */
  getContext(): FlowContext {
    return { ...this.flowState.context };
  }

  /** Replace the internal flow state (e.g. from persistence). */
  setState(state: FlowState) {
    this.flowState = state;

    // Restore the sequence counter to avoid collisions with pre-existing sequences.
    const maxStepSeq = this.flowSteps.reduce((max, s) => Math.max(max, s.sequence ?? 0), 0);
    const maxCompletedSeq = state.completedSteps.reduce((max, s) => Math.max(max, s.sequence), 0);
    this.sequenceCounter = Math.max(maxStepSeq, maxCompletedSeq) + 1;
  }

  // ── Flow execution ─────────────────────────────────────────────────────

  /** Start or resume the flow from the current step. */
  startFlow() {
    // If already waiting for user input or an external trigger (recovery),
    // just ensure the chatbot input state is correct — don't re-execute the step.
    if (this.flowState.isWaitingForInput) {
      this.chatbot.enableInput();
      return;
    }
    if (this.flowState.isWaitingForTrigger) {
      this.chatbot.disableInput();
      return;
    }

    this.runCurrentStep();
  }

  /** Mark the current step complete and run the next one. */
  advanceFlow() {
    const step = this.flowSteps[this.flowState.currentStepIndex];
    if (step) {
      this.flowState.completedSteps.push(ChatbotFlowEngine.toRef(step));
    }
    this.flowState.currentStepIndex++;
    this.flowState.isWaitingForTrigger = null;
    this.flowState.isWaitingForInput = false;

    this.notifyStateChanged();
    this.runCurrentStep();
  }

  /**
   * Complete an external trigger (e.g. a modal confirming data).
   * Stores data in context, notifies the host, and advances.
   */
  completeTrigger(triggerId: string, data?: FlowContext) {
    if (this.flowState.isWaitingForTrigger !== triggerId) return;

    // Store data in flow context
    if (data) {
      Object.assign(this.flowState.context, data);
    }

    // Notify host for trigger-specific side effects
    this.onTriggerCompleted?.(triggerId, data);

    // Disable previous flow actions and advance
    const selectedActionId = data?.actionId as string | undefined;
    this.chatbot.disablePreviousActions(selectedActionId);
    this.advanceFlow();
  }

  /**
   * Called by the chatbot when the user sends a message.
   * Routes the text to the appropriate flow step.
   */
  handleUserInput(text: string) {
    if (!this.flowState.isWaitingForInput) return;

    const step = this.flowSteps[this.flowState.currentStepIndex];
    if (step?.contextKey) {
      this.flowState.context[step.contextKey] = text;
    }

    // If the current step is async (error-recovery path), retry it
    // instead of advancing past it.
    if (step?.type === 'async') {
      this.flowState.isWaitingForInput = false;
      this.notifyStateChanged();
      this.runCurrentStep();
      return;
    }

    // Notify host before advancing (e.g. to store open question answers).
    if (step) {
      this.onInputReceived?.(text, step);
    }

    this.advanceFlow();
  }

  /** Reset the engine to its initial state. */
  reset(steps?: FlowStepDefinition[]) {
    this.flowState = ChatbotFlowEngine.createInitialState();
    this.sequenceCounter = 0;
    if (steps) {
      this.flowSteps = [...steps];
      this.assignSequences(this.flowSteps);
    }
    this.notifyStateChanged();
  }

  // ── Internal ───────────────────────────────────────────────────────────

  /** Execute the step at the current index. */
  private runCurrentStep() {
    const step = this.flowSteps[this.flowState.currentStepIndex];
    if (!step) {
      // Flow complete
      this.flowState.currentStep = null;
      this.notifyStateChanged();
      return;
    }

    // Track current step ref in state
    this.flowState.currentStep = ChatbotFlowEngine.toRef(step);

    // Skip if condition is met
    if (step.skipIf?.(this.flowState.context)) {
      this.flowState.completedSteps.push(ChatbotFlowEngine.toRef(step));
      this.flowState.currentStepIndex++;
      this.notifyStateChanged();
      this.runCurrentStep();
      return;
    }

    this.executeStep(step);
  }

  /** Build the content object for a step's text — always rendered as markdown. */
  private stepTextContent(step: FlowStepDefinition): ChatMessageContent {
    return { type: 'markdown', markdown: step.text ?? '' };
  }

  /** Interpret a single step definition and drive the chatbot accordingly. */
  private executeStep(step: FlowStepDefinition) {
    switch (step.type) {
      case 'message':
        if (step.text) {
          this.chatbot.addAssistantMessage(this.stepTextContent(step));
        }
        this.advanceFlow();
        break;

      case 'action':
        this.chatbot.addMessage('assistant', {
          type: 'actions',
          text: step.text,
          actions: step.actions ?? [],
        });
        if (step.disableInput) this.chatbot.disableInput();

        if (step.waitForTrigger) {
          this.flowState.isWaitingForTrigger = step.waitForTrigger;
          this.notifyStateChanged();
        } else {
          this.advanceFlow();
        }
        break;

      case 'input':
        if (step.text) {
          this.chatbot.addAssistantMessage(this.stepTextContent(step));
        }
        this.chatbot.enableInput();
        this.flowState.isWaitingForInput = true;
        this.notifyStateChanged();
        break;

      case 'multiple-select':
        this.executeMultipleSelectStep(step);
        break;

      case 'async':
        this.executeAsyncStep(step);
        break;
    }
  }

  /** Render a multiple-select message and wait for the user to confirm their selection. */
  private executeMultipleSelectStep(step: FlowStepDefinition) {
    const options = (step.multipleSelectOptions ?? []).map((option) => ({
      id: option.id,
      label: option.label,
    }));

    this.chatbot.addMessage('assistant', {
      type: 'multiple-select',
      prompt: step.text ?? '',
      options,
    });

    if (step.disableInput) this.chatbot.disableInput();

    if (step.waitForTrigger) {
      this.flowState.isWaitingForTrigger = step.waitForTrigger;
      this.notifyStateChanged();
    } else {
      this.advanceFlow();
    }
  }

  /**
   * Called when the user confirms a multiple-select.
   * Resolves the waiting trigger and advances the flow.
   * NOTE: The user echo is already added by `selectOptions`, so we skip
   * `disablePreviousActions` (multiple-select locks itself via selectedIds).
   */
  private handleMultipleSelectConfirm(_messageId: string, optionIds: string[]) {
    if (!this.flowState.isWaitingForTrigger) return;

    const triggerId = this.flowState.isWaitingForTrigger;

    // Build label from the chatbot's multiple-select message options
    const { messages } = this.chatbot.state.getValue();
    const multipleSelectMsg = [...messages].reverse().find((message) => message.content.type === 'multiple-select');
    const labels =
      multipleSelectMsg?.content.type === 'multiple-select'
        ? multipleSelectMsg.content.options
            .filter((option) => optionIds.includes(option.id))
            .map((option) => option.label)
        : [];

    const data: FlowContext = {
      actionId: optionIds.join(','),
      actionLabel: labels.join(', '),
      selectedOptionIds: optionIds,
      isMultipleSelect: true,
    };

    // Merge into flow context
    Object.assign(this.flowState.context, data);

    // Notify host for side effects (e.g. storing answers)
    this.onTriggerCompleted?.(triggerId, data);

    // Advance — no disablePreviousActions needed (multiple-select locks via selectedIds)
    this.advanceFlow();
  }

  /**
   * Execute an async step: show thinking indicator, run handler,
   * merge returned context, insert dynamic steps, then advance.
   */
  private async executeAsyncStep(step: FlowStepDefinition) {
    if (!step.handler) {
      this.advanceFlow();
      return;
    }

    this.chatbot.disableInput();
    this.chatbot.setThinking(true, step.thinkingMessages);

    try {
      const result = await step.handler(this.flowState.context);

      this.chatbot.setThinking(false);

      // Merge returned context data
      if (result.context) {
        Object.assign(this.flowState.context, result.context);
        this.onContextUpdated?.(result.context);
      }

      // Insert dynamic steps immediately after the current step
      if (result.insertSteps && result.insertSteps.length > 0) {
        const newSteps = result.insertSteps;
        this.assignSequences(newSteps);
        const currentIndex = this.flowState.currentStepIndex;
        this.flowSteps.splice(currentIndex + 1, 0, ...newSteps);
      }

      this.advanceFlow();
    } catch (error) {
      this.chatbot.setThinking(false);

      const message = error instanceof Error ? error.message : 'Er is een fout opgetreden.';
      this.chatbot.addAssistantMessage({ type: 'markdown', markdown: message });

      // Re-enable input so the user can retry.
      this.chatbot.enableInput();
      this.flowState.isWaitingForInput = true;
      this.notifyStateChanged();
    }
  }

  /** Notify state changed (for host to persist / sync). */
  private _onStateChanged?: () => void;

  /** Set a callback that fires whenever the internal flow state changes. */
  set onStateChanged(callback: (() => void) | undefined) {
    this._onStateChanged = callback;
  }

  private notifyStateChanged() {
    this._onStateChanged?.();
  }
}
